import React, { useState } from 'react';
import { List, TextField, FunctionField, TextInput, useTranslate } from 'react-admin';
import { Stack, Typography } from '@mui/material';
import { EditRounded as EditIcon, DeleteOutlineRounded as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '@/utils/httpClient';
import {
  ConfirmDeleteDialog, ListScreen, PageTitle, ResponsiveDatagrid, RowActionsMenu, SectionHeader, StatusDot,
} from '@/components/shell';

interface LicenseServer {
  id: number | string;
  name: string;
  status: string;
}

interface LicenseRecord {
  id: number | string;
  licenseName: string;
  servers?: LicenseServer[];
}

/** Each server the license may use, with whether it is reachable. */
const LicenseServers: React.FC<{ record: LicenseRecord }> = ({ record }) => {
  const t = useTranslate();
  const servers = record.servers ?? [];
  if (!servers.length) {
    return (
      <Typography variant="body2" color="text.secondary" component="span">
        {t('screens.licenses.noServers')}
      </Typography>
    );
  }
  return (
    <Stack direction="row" columnGap={2} rowGap={0.5} flexWrap="wrap" useFlexGap component="span">
      {servers.map((s) => (
        <StatusDot key={s.id} tone={s.status === 'Online' ? 'ok' : 'problem'} label={s.name} />
      ))}
    </Stack>
  );
};

const LicenseActions: React.FC<{ record: LicenseRecord }> = ({ record }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            key: 'edit',
            label: t('ra.action.edit'),
            icon: <EditIcon fontSize="small" />,
            onClick: () => navigate(`/licenses/${record.id}`),
          },
          {
            key: 'delete',
            label: t('ra.action.delete'),
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            dividerBefore: true,
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />
      <ConfirmDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        name={record.licenseName}
        onDelete={() => httpClient.delete(`/admin/license/DeleteLicense/${record.id}`)}
      />
    </>
  );
};

export const LicenseList = () => {
  const t = useTranslate();
  const navigate = useNavigate();

  const filters = [<TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />];

  return (
    <ListScreen
      header={
        <SectionHeader
          left={
            <PageTitle title={t('resources.licenses.name', { smart_count: 2 })} subtitle={t('screens.licenses.subtitle')} />
          }
          create={{ label: t('common.create'), onClick: () => navigate('/licenses/create') }}
        />
      }
    >
      <List filters={filters} actions={false} empty={false} perPage={25} sort={{ field: 'licenseName', order: 'ASC' }}>
        <ResponsiveDatagrid<LicenseRecord>
          rowClick="edit"
          mobile={{
            primary: (r) => r.licenseName,
            lines: [
              { label: t('resources.licenses.fields.servers'), render: (r) => <LicenseServers record={r} /> },
            ],
            actions: (r) => <LicenseActions record={r} />,
            onClick: (r) => navigate(`/licenses/${r.id}`),
          }}
        >
          <TextField source="licenseName" label={t('resources.licenses.fields.licenseName')} sortBy="licenseName" />
          <FunctionField
            label={t('resources.licenses.fields.servers')}
            sortable={false}
            render={(r: LicenseRecord) => <LicenseServers record={r} />}
          />
          <FunctionField
            label={t('screens.licenses.serverCount')}
            sortable={false}
            textAlign="right"
            render={(r: LicenseRecord) => r.servers?.length ?? 0}
          />
          <FunctionField
            label=""
            textAlign="right"
            sortable={false}
            render={(r: LicenseRecord) => <LicenseActions record={r} />}
          />
        </ResponsiveDatagrid>
      </List>
    </ListScreen>
  );
};
